import { get } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getDateFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import {
    CHANNEL_EVENT_TYPES,
    CHANNEL_EVENT_FIELDS,
    CHANNEL_OCCURRENCE_FIELDS,
    CONTENT_OCCURRENCE_KIND,
    ENGAGEMENT_BUNDLE_EVENT_TYPES,
    CHANNEL_APP_TYPES,
    VIDEO_PLAY_STATUS,
    CHANNEL_COLORS,
    CHANNEL_COLOR_PALETTE,
    CHANNEL_COLOR_FALLBACK,
} from '@/constants/device';

dayjs.extend(utc);
dayjs.extend(timezone);

const LocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Resolve the event timestamp (epoch ms). Despite its name, the live events API
 * returns `utc_timestamp_inms` as an ISO-8601 string (not an integer), so we try
 * numeric epoch-ms first (in case a future/other environment sends a real
 * number), then fall back to date parsing. `send_time`/`recv_time` are ISO
 * fallbacks if `utc_timestamp_inms` is missing entirely.
 * @param {Object} event raw user event
 * @returns {number|null} epoch milliseconds or null when unparseable
 */
export const getEventTimestampMs = (event) => {
    if (!event) return null;
    const raw = event.utc_timestamp_inms;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string' && raw.trim() !== '') {
        if (/^\d+$/.test(raw.trim())) {
            const asNumber = Number(raw);
            if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
        }
        const parsed = Date.parse(raw);
        if (Number.isFinite(parsed)) return parsed;
    }
    const sendTime = Date.parse(event.send_time);
    if (Number.isFinite(sendTime)) return sendTime;
    const recvTime = Date.parse(event.recv_time);
    if (Number.isFinite(recvTime)) return recvTime;
    return null;
};

/**
 * Format a millisecond duration as a human string, e.g. "2 hr 15 min". Zero
 * components are omitted (e.g. "2 hr" not "2 hr 0 min", "45 min" not "0 hr 45 min").
 * @param {number} ms duration in milliseconds
 * @returns {string}
 */
export const formatWatchDuration = (ms) => {
    const value = Number(ms);
    if (!Number.isFinite(value) || value <= 0) return '0 min';
    if (value < MS_PER_MINUTE) return '< 1 min';
    const totalMinutes = Math.floor(value / MS_PER_MINUTE);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours} hr ${minutes} min`;
    if (hours > 0) return `${hours} hr`;
    return `${minutes} min`;
};

/**
 * Format a minutes value (float) into a compact chart axis-tick label, e.g.
 * 90 → "1h 30m", 60 → "1h", 15 → "15m", 0 → "0". Always collapses to whole
 * minutes/hours — never shows decimal precision.
 * @param {number} minutes
 * @returns {string}
 */
export const formatMinutesCompact = (minutes) => {
    const value = Number(minutes);
    if (!Number.isFinite(value) || value <= 0) return '0';
    const totalMinutes = Math.round(value);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
};

// Candidate "nice" minute step sizes for chart y-axes so gridlines/ticks land
// on meaningful increments (15m/30m/1h/2h/...) instead of arbitrary decimals.
const NICE_MINUTE_STEPS = [5, 10, 15, 30, 60, 120, 180, 240, 360, 480, 720, 1440];

/**
 * Pick a "nice" minute step size for a y-axis given its max value, targeting
 * roughly 4-6 gridlines.
 * @param {number} maxMinutes
 * @returns {number} step size in minutes
 */
export const getNiceMinuteStep = (maxMinutes) => {
    const max = Number(maxMinutes);
    if (!Number.isFinite(max) || max <= 0) return 15;
    const targetTicks = 5;
    const rawStep = max / targetTicks;
    const step = NICE_MINUTE_STEPS.find((candidate) => candidate >= rawStep);
    return step || NICE_MINUTE_STEPS[NICE_MINUTE_STEPS.length - 1];
};

/**
 * Format a percentage value clamped to [0, 100]; null/undefined → "—".
 * @param {number|null|undefined} n percent value
 * @returns {string}
 */
export const formatPercent = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
    const clamped = Math.min(100, Math.max(0, Number(n)));
    return `${Math.round(clamped)}%`;
};

/**
 * Deterministically hash a string to a non-negative integer (djb2-ish).
 * @param {string} str
 * @returns {number}
 */
const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
        hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    }
    return hash;
};

/**
 * Resolve the display color for a channel, falling back to a deterministic
 * pick from CHANNEL_COLOR_PALETTE so every distinct channel gets its own
 * stable color even when its id isn't in the curated CHANNEL_COLORS map
 * (real-world channel ids rarely match the curated list).
 * @param {string} channelId
 * @param {string} [channelName] used for hashing when channelId is missing
 * @returns {string} hex color
 */
export const getChannelColor = (channelId, channelName) => {
    if (channelId && CHANNEL_COLORS[channelId]) return CHANNEL_COLORS[channelId];
    const key = channelId || channelName;
    if (!key) return CHANNEL_COLOR_FALLBACK;
    const index = hashString(String(key)) % CHANNEL_COLOR_PALETTE.length;
    return CHANNEL_COLOR_PALETTE[index];
};

/**
 * Build a color assignment for a specific set of channels/apps (one call per
 * aggregate() result) that guarantees no two entries share a color as long as
 * there are no more entries than CHANNEL_COLOR_PALETTE has colors. A plain
 * per-entry hash (see getChannelColor above) can't guarantee this on its own —
 * two unrelated channels can hash to the same palette index (this is exactly
 * what caused Discover Live/Zinnia/Memory Lane to all render green). Curated
 * CHANNEL_COLORS matches are honored first, then remaining entries get a
 * hash-seeded starting point that probes forward through the palette to the
 * next color not already taken.
 * @param {Array<{key:string, id:?string, name:?string}>} entries unique color keys
 * @returns {Object<string,string>} map of key -> hex color
 */
export const buildChannelColorMap = (entries = []) => {
    const colorByKey = {};
    const usedColors = new Set();

    entries.forEach(({ key, id }) => {
        if (id && CHANNEL_COLORS[id]) {
            colorByKey[key] = CHANNEL_COLORS[id];
            usedColors.add(colorByKey[key]);
        }
    });

    entries.forEach(({ key, id, name }) => {
        if (colorByKey[key]) return;
        const seed = id || name || key;
        const baseIndex = hashString(String(seed)) % CHANNEL_COLOR_PALETTE.length;
        let color = null;
        for (let offset = 0; offset < CHANNEL_COLOR_PALETTE.length; offset += 1) {
            const candidate =
                CHANNEL_COLOR_PALETTE[(baseIndex + offset) % CHANNEL_COLOR_PALETTE.length];
            if (!usedColors.has(candidate)) {
                color = candidate;
                break;
            }
        }
        // Palette exhausted (more distinct channels/apps than curated colors) —
        // fall back to the hash pick even if it collides with another entry.
        color = color || CHANNEL_COLOR_PALETTE[baseIndex];
        colorByKey[key] = color;
        usedColors.add(color);
    });

    return colorByKey;
};

/**
 * Derive up to two uppercase initials from a channel name.
 * "Discover Live" → "DL", "Spiro 100" → "S1"; empty → "?".
 * @param {string} channelName
 * @returns {string}
 */
export const getChannelInitials = (channelName) => {
    if (!channelName || typeof channelName !== 'string') return '?';
    const tokens = channelName.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return '?';
    if (tokens.length === 1) {
        const token = tokens[0];
        return token.length >= 2 ? token.slice(0, 2).toUpperCase() : token[0].toUpperCase();
    }
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
};

const toFiniteNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

/**
 * Normalize a terminal event (completed | abandoned) into a common shape.
 * @param {Object} event raw user event
 * @returns {Object|null}
 */
export const normalizeTerminalEvent = (event) => {
    const ts = getEventTimestampMs(event);
    if (ts === null) return null;

    const { message_type: messageType } = event;
    if (messageType === CHANNEL_EVENT_TYPES.VIDEO_PLAY_COMPLETED) {
        const fields = CHANNEL_EVENT_FIELDS[CHANNEL_EVENT_TYPES.VIDEO_PLAY_COMPLETED];
        const videoId = get(event, fields.video_id, null);
        return {
            eventId: event.message_id || `completed-${videoId}-${ts}`,
            ts,
            videoId,
            videoTitle: get(event, fields.video_title, null),
            channelId: get(event, fields.channel_id, null),
            channelName: get(event, fields.channel_name, null),
            watchMs: toFiniteNumber(get(event, fields.total_watch_time_ms)),
            percentWatched: 100,
            status: VIDEO_PLAY_STATUS.COMPLETED,
        };
    }

    if (messageType === CHANNEL_EVENT_TYPES.VIDEO_PLAY_ABANDONED) {
        const fields = CHANNEL_EVENT_FIELDS[CHANNEL_EVENT_TYPES.VIDEO_PLAY_ABANDONED];
        const rawPercent = get(event, fields.percent_watched, null);
        const videoId = get(event, fields.video_id, null);
        return {
            eventId: event.message_id || `abandoned-${videoId}-${ts}`,
            ts,
            videoId,
            videoTitle: get(event, fields.video_title, null),
            channelId: get(event, fields.channel_id, null),
            channelName: get(event, fields.channel_name, null),
            watchMs: toFiniteNumber(get(event, fields.watch_duration_ms)),
            percentWatched: rawPercent === null || rawPercent === undefined ? null : Number(rawPercent),
            status: VIDEO_PLAY_STATUS.ABANDONED,
        };
    }

    return null;
};

/**
 * Normalize an occurrence-only event (channel opened, or third-party app opened)
 * into a common shape. Unlike normalizeTerminalEvent, these carry NO watch
 * duration or completion outcome — the underlying app (e.g. a licensed Coro
 * Health web app) doesn't report playback telemetry back to us, so all we
 * genuinely know is that it was launched at a point in time. Native app
 * launches (app_type === 'native') are excluded — those are our own app and
 * already tracked via the video-play lifecycle events above.
 * @param {Object} event raw user event
 * @returns {Object|null}
 */
export const normalizeOccurrenceEvent = (event) => {
    const ts = getEventTimestampMs(event);
    if (ts === null) return null;

    const { message_type: messageType } = event;

    if (messageType === CHANNEL_EVENT_TYPES.CHANNEL_LAUNCHED) {
        const fields = CHANNEL_OCCURRENCE_FIELDS[CHANNEL_EVENT_TYPES.CHANNEL_LAUNCHED];
        const channelId = get(event, fields.channel_id, null);
        const channelName = get(event, fields.channel_name, null);
        return {
            eventId: event.message_id || `launched-${channelId || channelName}-${ts}`,
            ts,
            kind: CONTENT_OCCURRENCE_KIND.CHANNEL,
            channelId,
            channelName,
            appName: null,
            appDetails: null,
        };
    }

    if (messageType === ENGAGEMENT_BUNDLE_EVENT_TYPES.APP_LAUNCH) {
        const fields = CHANNEL_OCCURRENCE_FIELDS[ENGAGEMENT_BUNDLE_EVENT_TYPES.APP_LAUNCH];
        const appType = get(event, fields.app_type, null);
        if (appType === CHANNEL_APP_TYPES.NATIVE) return null;
        const appName = get(event, fields.app_name, null);
        return {
            eventId: event.message_id || `app-${appName}-${ts}`,
            ts,
            kind: CONTENT_OCCURRENCE_KIND.APP,
            channelId: null,
            channelName: null,
            appName,
            appDetails: get(event, fields.app_details, null),
        };
    }

    return null;
};

/**
 * Build an index of started events keyed by video_id, each sorted ascending by ts.
 * @param {Array<Object>} startedEvents raw started events
 * @returns {Map<string, Array<{ ts:number, themeName:string|null, durationMs:number }>>}
 */
export const buildStartedIndexByVideo = (startedEvents = []) => {
    const fields = CHANNEL_EVENT_FIELDS[CHANNEL_EVENT_TYPES.VIDEO_PLAY_STARTED];
    const index = new Map();
    startedEvents.forEach((event) => {
        const ts = getEventTimestampMs(event);
        if (ts === null) return;
        const videoId = get(event, fields.video_id, null);
        if (!videoId) return;
        const entry = {
            ts,
            themeName: get(event, fields.theme_name, null),
            durationMs: toFiniteNumber(get(event, fields.duration_ms)),
        };
        if (!index.has(videoId)) index.set(videoId, []);
        index.get(videoId).push(entry);
    });
    index.forEach((entries) => entries.sort((a, b) => a.ts - b.ts));
    return index;
};

/**
 * Enrich a normalized terminal row with themeName/videoDurationMs from the latest
 * started event of the same videoId whose ts ≤ row.ts.
 * @param {Object} normalized normalized terminal row
 * @param {Map} startedIndex output of buildStartedIndexByVideo
 * @returns {Object} enriched row
 */
export const enrichWithStarted = (normalized, startedIndex) => {
    const enriched = { ...normalized, themeName: null, videoDurationMs: null };
    if (!normalized.videoId || !startedIndex.has(normalized.videoId)) return enriched;
    const entries = startedIndex.get(normalized.videoId);
    let match = null;
    for (let i = 0; i < entries.length; i += 1) {
        if (entries[i].ts <= normalized.ts) match = entries[i];
        else break;
    }
    if (match) {
        enriched.themeName = match.themeName;
        enriched.videoDurationMs = match.durationMs;
    }
    return enriched;
};

const getLocalDateKey = (ts, tz) => dayjs(ts).tz(tz).format('YYYY-MM-DD');
const getLocalHour = (ts, tz) => dayjs(ts).tz(tz).hour();

/**
 * Aggregate raw content events into the model consumed by the three sub-views.
 * @param {Array<Object>} rawEvents raw user events (started ∪ completed ∪ abandoned)
 * @param {string} timezone IANA timezone
 * @param {{ fromMs?: number, toMs?: number }} [window] optional window bounds for the daily series
 * @returns {Object} { summary, historyByDate, daily, hourly }
 */
export const aggregate = (rawEvents = [], timezone, window = {}) => {
    const tz = timezone || LocalTimezone;

    // De-dupe by message_id.
    const seen = new Set();
    const events = [];
    (rawEvents || []).forEach((event) => {
        const id = event?.message_id;
        if (id && seen.has(id)) return;
        if (id) seen.add(id);
        events.push(event);
    });

    const started = [];
    const completed = [];
    const abandoned = [];
    const channelLaunched = [];
    const appLaunched = [];
    events.forEach((event) => {
        switch (event?.message_type) {
            case CHANNEL_EVENT_TYPES.VIDEO_PLAY_STARTED:
                started.push(event);
                break;
            case CHANNEL_EVENT_TYPES.VIDEO_PLAY_COMPLETED:
                completed.push(event);
                break;
            case CHANNEL_EVENT_TYPES.VIDEO_PLAY_ABANDONED:
                abandoned.push(event);
                break;
            case CHANNEL_EVENT_TYPES.CHANNEL_LAUNCHED:
                channelLaunched.push(event);
                break;
            case ENGAGEMENT_BUNDLE_EVENT_TYPES.APP_LAUNCH:
                appLaunched.push(event);
                break;
            default:
                break;
        }
    });

    const startedIndex = buildStartedIndexByVideo(started);

    const terminal = [...completed, ...abandoned]
        .map(normalizeTerminalEvent)
        .filter(Boolean)
        .map((row) => enrichWithStarted(row, startedIndex));

    // Occurrence-only rows (channel opened / third-party app opened) — no
    // duration or completion data exists for these, see normalizeOccurrenceEvent.
    const occurrences = [...channelLaunched, ...appLaunched]
        .map(normalizeOccurrenceEvent)
        .filter(Boolean);
    const otherEngagementCount = occurrences.length;

    // COLORS — assign colors for every distinct channel/app appearing anywhere
    // in this result (video-play channels + channel-launch channels + app-launch
    // apps) in one pass, so the same channel always gets the same color across
    // History rows and Charts, and no two distinct channels/apps collide (see
    // buildChannelColorMap for why a plain per-item hash isn't enough).
    const colorEntries = [];
    const seenColorKeys = new Set();
    const addColorEntry = (key, id, name) => {
        if (!key || seenColorKeys.has(key)) return;
        seenColorKeys.add(key);
        colorEntries.push({ key, id, name });
    };
    terminal.forEach((r) => addColorEntry(r.channelId || r.channelName || 'unknown', r.channelId, r.channelName));
    occurrences.forEach((r) => {
        if (r.kind === CONTENT_OCCURRENCE_KIND.APP) {
            addColorEntry(`app:${r.appName || 'unknown-app'}`, null, r.appName);
        } else {
            addColorEntry(r.channelId || r.channelName || 'unknown', r.channelId, r.channelName);
        }
    });
    const channelColors = buildChannelColorMap(colorEntries);
    const colorForRow = (r) =>
        r.kind === CONTENT_OCCURRENCE_KIND.APP
            ? channelColors[`app:${r.appName || 'unknown-app'}`]
            : channelColors[r.channelId || r.channelName || 'unknown'];

    // Counts derive from normalized terminal rows (ts-valid only).
    const completedRows = terminal.filter((r) => r.status === VIDEO_PLAY_STATUS.COMPLETED);
    const abandonedRows = terminal.filter((r) => r.status === VIDEO_PLAY_STATUS.ABANDONED);

    // SUMMARY
    const totalWatchMs = terminal.reduce((sum, r) => sum + r.watchMs, 0);
    const completedCount = completedRows.length;
    const abandonedCount = abandonedRows.length;
    const sessionCount = terminal.length;
    const completionRate = sessionCount ? completedCount / sessionCount : null;
    const activeDays = new Set(terminal.map((r) => getLocalDateKey(r.ts, tz))).size;
    const avgSessionMs = sessionCount ? totalWatchMs / sessionCount : 0;

    const channelAgg = new Map();
    terminal.forEach((r) => {
        const key = r.channelId || r.channelName || 'unknown';
        const existing = channelAgg.get(key) || {
            channelId: r.channelId,
            channelName: r.channelName,
            watchMs: 0,
            lastTs: 0,
        };
        existing.watchMs += r.watchMs;
        if (r.ts > existing.lastTs) existing.lastTs = r.ts;
        if (!existing.channelName && r.channelName) existing.channelName = r.channelName;
        channelAgg.set(key, existing);
    });
    let favoriteChannel = null;
    channelAgg.forEach((agg) => {
        if (
            !favoriteChannel ||
            agg.watchMs > favoriteChannel.watchMs ||
            (agg.watchMs === favoriteChannel.watchMs && agg.lastTs > favoriteChannel.lastTs)
        ) {
            favoriteChannel = agg;
        }
    });
    if (favoriteChannel) {
        favoriteChannel = {
            channelId: favoriteChannel.channelId,
            channelName: favoriteChannel.channelName,
            watchMs: favoriteChannel.watchMs,
        };
    }

    // HISTORY — group by local date, groups desc, items desc by ts. Terminal
    // (duration-based) rows and occurrence-only rows (rowKind !== 'session')
    // are merged into the same date groups so a day shows everything that
    // happened, in chronological order — the row renderer distinguishes them.
    const historyGroups = new Map();
    const historyRows = [
        ...terminal.map((r) => ({ ...r, rowKind: 'session', color: colorForRow(r) })),
        ...occurrences.map((r) => ({ ...r, rowKind: r.kind, color: colorForRow(r) })),
    ];
    historyRows.forEach((r) => {
        const dateKey = getLocalDateKey(r.ts, tz);
        if (!historyGroups.has(dateKey)) historyGroups.set(dateKey, []);
        historyGroups.get(dateKey).push(r);
    });
    const historyByDate = Array.from(historyGroups.entries())
        .sort((a, b) => (a[0] < b[0] ? 1 : -1))
        .map(([dateKey, items]) => ({
            dateKey,
            dateLabel: getDateFormat(items[0].ts, tz).dayDateformat,
            items: items.slice().sort((a, b) => b.ts - a.ts),
        }));

    // DAILY — every date in the window (inclusive), including zero days.
    const dailyMap = new Map();
    terminal.forEach((r) => {
        const dateKey = getLocalDateKey(r.ts, tz);
        if (!dailyMap.has(dateKey))
            dailyMap.set(dateKey, { byChannel: {}, totalMs: 0, otherEngagementCount: 0 });
        const bucket = dailyMap.get(dateKey);
        const channelKey = r.channelId || r.channelName || 'unknown';
        bucket.byChannel[channelKey] = (bucket.byChannel[channelKey] || 0) + r.watchMs;
        bucket.totalMs += r.watchMs;
    });
    occurrences.forEach((r) => {
        const dateKey = getLocalDateKey(r.ts, tz);
        if (!dailyMap.has(dateKey))
            dailyMap.set(dateKey, { byChannel: {}, totalMs: 0, otherEngagementCount: 0 });
        dailyMap.get(dateKey).otherEngagementCount += 1;
    });

    let fromMs = window.fromMs;
    let toMs = window.toMs;
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
        const tsList = terminal.map((r) => r.ts);
        if (tsList.length) {
            fromMs = Math.min(...tsList);
            toMs = Math.max(...tsList);
        }
    }

    const daily = [];
    if (Number.isFinite(fromMs) && Number.isFinite(toMs)) {
        let cursor = dayjs(fromMs).tz(tz).startOf('day');
        const end = dayjs(toMs).tz(tz).startOf('day');
        // Guard against pathological windows.
        let guard = 0;
        const maxDays = Math.floor((toMs - fromMs) / MS_PER_DAY) + 2;
        while ((cursor.isBefore(end) || cursor.isSame(end, 'day')) && guard <= maxDays) {
            const dateKey = cursor.format('YYYY-MM-DD');
            const bucket = dailyMap.get(dateKey) || { byChannel: {}, totalMs: 0, otherEngagementCount: 0 };
            daily.push({
                date: dateKey,
                label: cursor.format('MMM D'),
                byChannel: bucket.byChannel,
                totalMs: bucket.totalMs,
                otherEngagementCount: bucket.otherEngagementCount,
            });
            cursor = cursor.add(1, 'day');
            guard += 1;
        }
    }

    // HOURLY — 24 buckets (local tz), broken down by channel so the "time of
    // day" chart can depict *which* channel was engaged with at *which* hour.
    const hourly = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        watchMs: 0,
        sessions: 0,
        byChannel: {},
        otherEngagementCount: 0,
    }));
    terminal.forEach((r) => {
        const hour = getLocalHour(r.ts, tz);
        const bucket = hourly[hour];
        bucket.watchMs += r.watchMs;
        bucket.sessions += 1;
        const channelKey = r.channelId || r.channelName || 'unknown';
        bucket.byChannel[channelKey] = (bucket.byChannel[channelKey] || 0) + r.watchMs;
    });
    occurrences.forEach((r) => {
        const hour = getLocalHour(r.ts, tz);
        hourly[hour].otherEngagementCount += 1;
    });

    return {
        summary: {
            totalWatchMs,
            sessionCount,
            completionRate,
            completedCount,
            abandonedCount,
            activeDays,
            avgSessionMs,
            favoriteChannel,
            otherEngagementCount,
        },
        channels: Array.from(channelAgg.entries()).map(([key, agg]) => ({
            key,
            channelId: agg.channelId,
            channelName: agg.channelName,
            watchMs: agg.watchMs,
            color: channelColors[key],
        })),
        historyByDate,
        daily,
        hourly,
    };
};
